import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const GREETING = "Hi! 👋 Ask me anything about leasing, financing, shipping, returns, warranty, or our phones.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "bot", text: GREETING }]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/faqs").then((r) => setSuggestions((r.data || []).slice(0, 4).map((f) => f.question))).catch(() => {});
  }, []);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, loading]);

  const ask = async (question) => {
    const q = (question || "").trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/faq/ask", { question: q });
      if (data.matched) {
        setMessages((m) => [...m, { role: "bot", text: data.answer, related: (data.related || []).map((r) => r.question) }]);
      } else {
        setMessages((m) => [...m, { role: "bot", text: "I'm not sure about that one — but our team can help! Leave your details below and we'll get right back to you.", lead: true, question: q }]);
      }
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Something went wrong. Leave your details below and our team will help you out.", lead: true, question: q }]);
    } finally {
      setLoading(false);
    }
  };

  const onLeadSent = (email) => {
    setMessages((m) => [...m, { role: "bot", text: `Thanks! We've sent your message to our team${email ? ` and will reply to ${email}` : ""} soon. 🎉` }]);
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-launcher"
        aria-label="Open support chat"
        className="fixed bottom-5 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-crimson text-white shadow-xl transition-transform hover:scale-105 active:scale-95"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-5 z-[60] flex h-[70vh] max-h-[560px] w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-line bg-white shadow-2xl"
        >
          <div className="flex items-center gap-3 bg-ink px-5 py-4 text-white">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-crimson"><MessageCircle className="h-5 w-5" /></span>
            <div>
              <div className="font-display text-sm font-black leading-tight">Adopt-A-Phone Help</div>
              <div className="text-xs text-white/60">Typically replies instantly</div>
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto bg-alt px-4 py-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-crimson text-white" : "border border-line bg-white text-ink"}`}
                >
                  {m.text}
                  {m.lead && <LeadForm question={m.question} onSent={onLeadSent} />}
                  {m.related && m.related.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Related</div>
                      {m.related.map((r) => (
                        <button key={r} onClick={() => ask(r)} className="block w-full rounded-lg border border-line bg-alt px-3 py-1.5 text-left text-xs font-medium text-ink hover:border-crimson">
                          {r}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Suggested starters (only before any user message) */}
            {messages.length === 1 && suggestions.length > 0 && (
              <div className="space-y-1.5 pt-1" data-testid="chat-suggestions">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => ask(s)} className="block w-full rounded-xl border border-line bg-white px-3 py-2 text-left text-sm font-medium text-ink transition-colors hover:border-crimson">
                    {s}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="flex justify-start">
                <div className="rounded-2xl border border-line bg-white px-4 py-2.5 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); ask(input); }}
            className="flex items-center gap-2 border-t border-line bg-white p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="chat-input"
              placeholder="Type your question…"
              className="flex-1 rounded-full border border-line px-4 py-2.5 text-sm outline-none focus:border-crimson"
            />
            <button type="submit" disabled={loading || !input.trim()} data-testid="chat-send" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-crimson text-white disabled:opacity-40">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function LeadForm({ question, onSent }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: question || "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email, and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, interest: "Chat inquiry" });
      setDone(true);
      onSent?.(form.email);
    } catch {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return null;

  return (
    <form onSubmit={submit} data-testid="chat-lead-form" className="mt-3 space-y-2">
      <input value={form.name} onChange={set("name")} data-testid="chat-lead-name" placeholder="Your name" className="w-full rounded-lg border border-line px-3 py-2 text-xs text-ink outline-none focus:border-crimson" />
      <input type="email" value={form.email} onChange={set("email")} data-testid="chat-lead-email" placeholder="Email address" className="w-full rounded-lg border border-line px-3 py-2 text-xs text-ink outline-none focus:border-crimson" />
      <input value={form.phone} onChange={set("phone")} data-testid="chat-lead-phone" placeholder="Phone (optional)" className="w-full rounded-lg border border-line px-3 py-2 text-xs text-ink outline-none focus:border-crimson" />
      <textarea rows={2} value={form.message} onChange={set("message")} data-testid="chat-lead-message" placeholder="Your question" className="w-full resize-none rounded-lg border border-line px-3 py-2 text-xs text-ink outline-none focus:border-crimson" />
      <button type="submit" disabled={submitting} data-testid="chat-lead-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-crimson px-4 py-2 text-xs font-bold text-white disabled:opacity-60">
        {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…</> : "Send message"}
      </button>
    </form>
  );
}
